import React, { createContext, useState, useContext, useCallback, useEffect, useRef } from 'react';
import { Market, FilterType, FeaturedMarket } from '../types';
import { fetchFeaturedMarkets, fetchMarkets } from '../services/api';

const DEBUG = false;

export const emptyMarketData = {
  GameId: 0,
  Title: '',
  Name: '',
  Symbol: '',
  Description: '',
  Volume: 0,
  Supply: 0,
  CurrentPrice: 0,
  LogoUrl: '',
  StoreUrl: '',
  Platform: '',
  CreatorAddress: '',
  MarketCap: 0,
  MarketCapChange: 0,
  BondingCurveProgress: 0,
  TraderSentiment: 0,
  PercentageChange: 0,
  LaunchTime: new Date('2023-09-12').toTimeString(),  // Make this optional for now
  Created_at: new Date('2023-09-12').toTimeString(),
  isListedOnSwap: 0,
  ContractAddress: ''
};

interface MarketContextType {
  markets: Market[];
  loading: boolean;
  searching: boolean;
  error: string | null;
  filter: FilterType;
  setFilter: (filter: FilterType) => void;
  setSearching: (searching: boolean) => void;
  setMarkets: (markets: Market[]) => void;
}

interface FeaturedMarketContextType {
  featuredMarket: FeaturedMarket;
  featuredLoading: boolean;
  featuredError: string | null;
}

const MarketContext = createContext<MarketContextType | undefined>(undefined);
const FeaturedMarketContext = createContext<FeaturedMarketContextType | undefined>(undefined);

export const useMarketContext = () => {
  const context = useContext(MarketContext);
  if (!context) {
    throw new Error('useMarketContext must be used within a MarketProvider');
  }
  return context;
};

export const useFeaturedMarketContext = () => {
  const context = useContext(FeaturedMarketContext);
  if (!context) {
    throw new Error('useFeaturedMarketContext must be used within a FeaturedMarketProvider');
  }
  return context;
};

export const MarketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [markets, setMarkets] = useState<Market[]>([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterType>('new');

  const lastFetchedFilter = useRef<FilterType | null>(null);
  const fetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialMount = useRef(true);

  const fetchMarketsData = useCallback(async (currentFilter: FilterType) => {
    debugLog(`Fetching markets for filter: ${currentFilter}`);
    try {
      setLoading(true);
      const data = await fetchMarkets(currentFilter);
      setMarkets(data);
      setError(null);
      lastFetchedFilter.current = currentFilter;
    } catch (err) {
      setError('Failed to fetch markets');
      console.error('Error fetching markets:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const debouncedFetchMarkets = useCallback((currentFilter: FilterType) => {
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }

    fetchTimeoutRef.current = setTimeout(() => {
      if (lastFetchedFilter.current !== currentFilter) {
        fetchMarketsData(currentFilter).catch((err) => console.log(err.message));
      }
    }, 500); // 500ms debounce
  }, [fetchMarketsData]);

  useEffect(() => {
    debugLog(`Effect running with filter: ${filter}`);

    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchMarketsData(filter).catch((err) => console.log(err.message)); // Immediate fetch on initial mount
    } else {
      debouncedFetchMarkets(filter); // Debounced fetch for subsequent updates
    }

    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [filter, debouncedFetchMarkets, fetchMarketsData]);

  const debugLog = (message: string) => {
    if (DEBUG) {
      console.log(`[MarketContext] ${message}`);
    }
  };

  return (
    <MarketContext.Provider value={{ markets, loading, error, searching, setSearching, filter, setFilter, setMarkets }}>
      {children}
    </MarketContext.Provider>
  );
};

export const FeaturedMarketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [featuredMarket, setFeaturedMarket] = useState<FeaturedMarket>(emptyMarketData);
  const [featuredLoading, setLoading] = useState(true);
  const [featuredError, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadFeaturedMarket = async () => {
      try {
        setLoading(true);
        const data = await fetchFeaturedMarkets();
        setFeaturedMarket(data);
        setError(null);
      } catch (err) {
        setError('Failed to fetch markets');
        console.error('Error fetching markets:', err);
      } finally {
        setLoading(false);
      }
    };

    loadFeaturedMarket().catch((err) => console.log(err.message));
  }, []);

  return (
    <FeaturedMarketContext.Provider value={{ featuredMarket, featuredLoading, featuredError }}>
      {children}
    </FeaturedMarketContext.Provider>
  );
};